// Keys, credentials and encryption for the print server (see server/API.md,
// "Keys, authentication and encryption"). Everything is derived from one random key K:
//
//   R  = HKDF(K, "printy/v1/reader")         reader secret
//   D  = HKDF(K, "printy/v1/device")         device credential (sent by the ESP)
//   id = base32(HKDF(R, "printy/v1/id")[0..10])
//   Ra = HKDF(R, "printy/v1/auth")           reader credential (sent by the browser)
//   E  = HKDF(R, "printy/v1/enc")            AES-256-GCM key for chunks and names
//
// A browser that holds K can act as the device too; one that only holds R (a "reader"
// login link) can read, decrypt, rename and delete, but cannot push or re-pair a device.
// The server only ever sees id, D and Ra, so it cannot decrypt anything.

const subtle = globalThis.crypto.subtle;
const enc = new TextEncoder();
const dec = new TextDecoder();

// ---- encodings ----

export function b64urlEncode(bytes) {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function b64urlDecode(text) {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) throw new Error('not base64url');
  const s = atob(text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

// RFC 4648 base32 alphabet, lowercase, no padding.
export function base32(bytes) {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz234567';
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += alphabet[(buffer >> (bits - 5)) & 31];
      bits -= 5;
    }
    buffer &= (1 << bits) - 1;
  }
  return out;
}

const concat = (...parts) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
};

// ---- key derivation ----

async function hkdf(secret, info, bytes = 32) {
  const base = await subtle.importKey('raw', secret, 'HKDF', false, ['deriveBits']);
  const bits = await subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: enc.encode(info) }, base, bytes * 8);
  return new Uint8Array(bits);
}

export function generateKey() {
  return globalThis.crypto.getRandomValues(new Uint8Array(32));
}

// From the reader secret R: everything a reader needs. `enc` is a non-extractable AES-GCM key.
export async function deriveFromReader(R) {
  const id = base32((await hkdf(R, 'printy/v1/id')).subarray(0, 10));
  const readerAuth = b64urlEncode(await hkdf(R, 'printy/v1/auth'));
  const encBytes = await hkdf(R, 'printy/v1/enc');
  const key = await subtle.importKey('raw', encBytes, 'AES-GCM', false, ['encrypt', 'decrypt']);
  return { kind: 'reader', id, readerAuth, enc: key, R };
}

// From the full key K: the reader part plus the device credential. Holding K also lets the page
// give a new ESP the same identity (SET_KEY).
export async function deriveFromKey(K) {
  const R = await hkdf(K, 'printy/v1/reader');
  const reader = await deriveFromReader(R);
  const device = b64urlEncode(await hkdf(K, 'printy/v1/device'));
  return { ...reader, kind: 'full', K, device };
}

// What is stored for a paired printer: the secret (K or R) is enough to derive the rest.
export function secretOf(keys) {
  return keys.kind === 'full' ? keys.K : keys.R;
}
export async function deriveFromSecret(kind, secret) {
  if (secret.length !== 32) throw new Error('a key is 32 bytes');
  return kind === 'full' ? deriveFromKey(secret) : deriveFromReader(secret);
}

// ---- chunks ----
// Blob as stored by the server (and sent by the ESP):
//   u32 length (big endian, of everything after this field) | u8 version = 1 | nonce (12) | ciphertext | tag (16)
// AES-256-GCM, key E, associated data = the version byte. Plaintext:
//   u32 boot | u32 n | u8 flags (bit0 new job, bit1 cut) | u8 start reason (1 boot, 2 reset, 3 button) | stream bytes
const VERSION = 1;
const HEADER = 10;

export async function encryptChunk(encKey, { boot, n, newJob = 0, cut = false, bytes }, nonce = globalThis.crypto.getRandomValues(new Uint8Array(12))) {
  const plain = new Uint8Array(HEADER + bytes.length);
  const view = new DataView(plain.buffer);
  view.setUint32(0, boot >>> 0);
  view.setUint32(4, n >>> 0);
  plain[8] = (newJob ? 1 : 0) | (cut ? 2 : 0);
  plain[9] = newJob;
  plain.set(bytes, HEADER);
  const sealed = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: Uint8Array.of(VERSION), tagLength: 128 }, encKey, plain));
  const body = concat(Uint8Array.of(VERSION), nonce, sealed);
  const out = new Uint8Array(4 + body.length);
  new DataView(out.buffer).setUint32(0, body.length);
  out.set(body, 4);
  return out;
}

// The chunks in a stream of concatenated blobs (what the server's stream endpoint returns).
// Throws on a malformed or tampered blob.
export async function decryptChunks(encKey, bytes) {
  const chunks = [];
  let o = 0;
  while (o < bytes.length) {
    if (o + 4 > bytes.length) throw new Error('truncated chunk header');
    const len = new DataView(bytes.buffer, bytes.byteOffset + o, 4).getUint32(0);
    o += 4;
    if (len < 1 + 12 + 16 + HEADER || o + len > bytes.length) throw new Error('truncated or malformed chunk');
    const blob = bytes.subarray(o, o + len);
    o += len;
    if (blob[0] !== VERSION) throw new Error(`unknown chunk version ${blob[0]}`);
    const plain = new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: blob.subarray(1, 13), additionalData: Uint8Array.of(VERSION), tagLength: 128 }, encKey, blob.subarray(13)));
    const view = new DataView(plain.buffer, plain.byteOffset, plain.length);
    chunks.push({
      boot: view.getUint32(0),
      n: view.getUint32(4),
      newJob: (plain[8] & 1) !== 0 ? plain[9] : 0,
      cut: (plain[8] & 2) !== 0,
      bytes: plain.slice(HEADER),
    });
  }
  return chunks;
}

// ---- names (job and printer names are encrypted by the browser) ----
// base64url of: nonce (12) | ciphertext | tag. Associated data "n1".
const NAME_AAD = enc.encode('n1');

export async function encryptName(encKey, text, nonce = globalThis.crypto.getRandomValues(new Uint8Array(12))) {
  const sealed = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: NAME_AAD, tagLength: 128 }, encKey, enc.encode(text)));
  return b64urlEncode(concat(nonce, sealed));
}

export async function decryptName(encKey, text) {
  const bytes = b64urlDecode(text);
  if (bytes.length < 12 + 16) throw new Error('name too short');
  return dec.decode(await subtle.decrypt({ name: 'AES-GCM', iv: bytes.subarray(0, 12), additionalData: NAME_AAD, tagLength: 128 }, encKey, bytes.subarray(12)));
}

// ---- login links: https://server/#login=full:<K>,reader:<R>,... ----

export function buildLoginLink(origin, items) {
  const parts = items.map(({ kind, secret }) => `${kind}:${b64urlEncode(secret)}`);
  return `${origin.replace(/\/+$/, '')}/#login=${parts.join(',')}`;
}

// The items of a location hash ("#login=..."). Items of an unknown kind or with a malformed key
// are ignored; the result is [] when the hash is no login link.
export function parseLoginFragment(hash) {
  const m = /^#?login=(.*)$/.exec(hash);
  if (!m) return [];
  const items = [];
  for (const part of m[1].split(',')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const kind = part.slice(0, i);
    if (kind !== 'full' && kind !== 'reader') continue;
    try {
      const secret = b64urlDecode(part.slice(i + 1));
      if (secret.length === 32) items.push({ kind, secret });
    } catch {
      // malformed: ignore
    }
  }
  return items;
}
